<script setup>
import {ref, provide, watchEffect, useSlots, onMounted, watch, computed} from 'vue'

const props = defineProps({
    namestep: {
        type: String
    },
    classname: {
        type: String,
        default: null
    },
    trackState: {
        type: Boolean,
        required: false,
        default: true,
    },
    preselectedTab: {
        type: String,
        required: false,
        default: null,
    },
    stepsBackgroundClass: {
        type: String,
        required: false,
        default: 'bg-gray-100 border-gray-100',
    },
    displayProgressBar: {
        type: Boolean,
        required: false,
        default: false,
    },
    progressBarClass: {
        type: String,
        required: false,
        default: 'bg-wlPrimary top-1/2',
    }
})

const emit = defineEmits(['changestatusstep'])
const slots = useSlots();

const globalActionRightSlot = computed(() => slots['action-right-global'] ? slots['action-right-global']() : null)

const formIsFilled = ref(false)
const stepTitles = computed(() => {
    return slots.default().flatMap((tab) => tab.props !== null ? tab.props : [])
})
const isSelected = ref(Object.fromEntries(Object.entries(stepTitles.value).filter(([key, value]) => value.active === true )))
const selectedId = Object.keys(isSelected.value).length ? Object.keys(isSelected.value)[0] : null
const currentStepSlug = ref(stepTitles.value[selectedId] ? stepTitles.value[selectedId].value : null)

provide('currentStepSlug', currentStepSlug)

watchEffect(() => {
    if(currentStepSlug.value && formIsFilled.value) {
        emit('changestatusstep', currentStepSlug.value)
    }
    formIsFilled.value = true
})

const selectTab = (tab) => {
    currentStepSlug.value = tab
}

const navigateIfUnlocked = (tab) => {
    if (! tab.unlocked) {
        return
    }
    currentStepSlug.value = tab.value
}

const goToPrevious = () => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    if (currentIndex > 0) {
        const previousStep = stepTitles.value[currentIndex - 1]
        if (previousStep.unlocked) {
            currentStepSlug.value = previousStep.value
        }
    }
}

const goToNext = () => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    if (currentIndex < stepTitles.value.length - 1) {
        const nextStep = stepTitles.value[currentIndex + 1]
        if (nextStep.unlocked) {
            currentStepSlug.value = nextStep.value
        }
    }
}

const previousIsPresent = computed(() => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    return currentIndex > 0
})

const canGoToPrevious = computed(() => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    return currentIndex > 0 && stepTitles.value[currentIndex - 1]?.unlocked
})

const nextIsPresent = computed(() => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    return currentIndex < stepTitles.value.length - 1
})

const canGoToNext = computed(() => {
    const currentIndex = stepTitles.value.findIndex(step => step.value === currentStepSlug.value)
    return currentIndex < stepTitles.value.length - 1 && stepTitles.value[currentIndex + 1]?.unlocked
})

provide('goToPrevious', goToPrevious)
provide('goToNext', goToNext)
provide('previousIsPresent', previousIsPresent)
provide('canGoToPrevious', canGoToPrevious)
provide('nextIsPresent', nextIsPresent)
provide('canGoToNext', canGoToNext)
provide('globalActionRightSlot', globalActionRightSlot)

const verifyActive = (tab) => {
    if (tab.value === currentStepSlug.value) {
        return true
    }
}

watch(currentStepSlug, (newValue) => {
    if (! props.trackState) {
        return
    }
    if (newValue) {
        window.location.hash = newValue
    }
})

defineExpose({
    selectTab,
    goToPrevious,
    goToNext,
    canGoToPrevious,
    canGoToNext,
    previousIsPresent,
    nextIsPresent
})

const progressBarStyle = computed(() => {
    const currentIndex = (stepTitles.value.findIndex(step => step.value === currentStepSlug.value)) + 1
    const totalSteps = stepTitles.value.length
    const progressPercentage = ((currentIndex-1) / (totalSteps-1)) * 100
    return {right: 100-progressPercentage+'%'}
})

onMounted(() => {
    const presetTab = window.location.hash.replace('#', '')

    if (presetTab && stepTitles.value.find(tab => tab.value === presetTab)) {
        currentStepSlug.value = presetTab
    } else if (props.preselectedTab) {
        currentStepSlug.value = props.preselectedTab
    }
})

</script>
<template>
    <div class="@container w-full max-w-screen">
        <div class="flex justify-center w-full mb-6">
            <div class="relative w-full">
                <ul
                    v-bind:id="namestep" v-bind:class="classname"
                    class="flex justify-between w-full"
                >
                    <li
                        v-for="(step, index) in stepTitles"
                        class="z-10"
                    >
                        <ui-stepped-step-indicator
                            :key="index"
                            :first="index === 0"
                            :last="index === stepTitles.length - 1"
                            :is-completed="step.completed"
                            :is-active="verifyActive(step)"
                            :has-warning="step['has-warning'] || step.hasWarning"
                            :step="step"
                            :step-background-class="stepsBackgroundClass"
                            :tooltip="step.tooltip"
                            @selected="navigateIfUnlocked(step)"
                        />
                    </li>
                </ul>
                <div
                    class="bg-gray-300 h-px top-1/2 absolute left-0 right-0"
                ></div>
                <div
                    v-if="displayProgressBar"
                    class="absolute left-0 transition-all duration-500 ease-out rounded-md"
                    :class="progressBarClass"
                    :style="progressBarStyle"
                    style="height: 3px; transform: translateY(-1px);"
                ></div>
            </div>
        </div>
        <div class="mt-4">
            <slot/>
        </div>
    </div>
</template>
